import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { User } from '../../shared/types';

interface LoginForm {
  username: string;
  password: string;
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loginForm, setLoginForm] = useState<LoginForm>({ username: '', password: '' });
  const [isRegistering, setIsRegistering] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      if (api.isAuthenticated()) {
        const { user } = await api.getCurrentUser();
        setCurrentUser(user);
        setIsAuthenticated(true);
      }
    } catch {
      api.logout();
      console.log('No active session');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async () => {
    setAuthError('');
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setAuthError('Please enter both username and password');
      return;
    }

    try {
      const response = isRegistering
        ? await api.register(loginForm.username, loginForm.password)
        : await api.login(loginForm.username, loginForm.password);

      setCurrentUser(response.user);
      setIsAuthenticated(true);
      setLoginForm({ username: '', password: '' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Authentication error';
      setAuthError(message);
      console.error('Auth error:', error);
    }
  };

  const handleLogout = () => {
    api.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  const toggleRegistering = () => {
    setIsRegistering(!isRegistering);
    setAuthError('');
    setLoginForm({ username: '', password: '' });
  };

  return {
    isAuthenticated,
    currentUser,
    loginForm,
    setLoginForm,
    isRegistering,
    authError,
    isLoading,
    handleLogin,
    handleLogout,
    toggleRegistering,
  };
}
